import type { Message } from '../types/agent';

/**
 * Arquivo local de conversas (localStorage) — alimenta a "Home" estilo WhatsApp.
 * Cada conversa guarda o sessionId (pra retomar o mesmo contexto no gateway
 * enquanto o processo estiver vivo) e as mensagens saneadas.
 */
export type ConversaSalva = {
  id: string;
  sessionId: string;
  messages: Message[];
  updatedAt: number;
};

const KEY = 'mina_conversas_v1';
const MAX_CONVERSAS = 20;

/** Remove payloads pesados (base64 de foto/áudio/PDF) antes de persistir —
 * localStorage tem ~5MB; uma foto de receita estouraria o arquivo inteiro. */
function sanearMensagens(messages: Message[]): Message[] {
  return messages.map((m) => {
    const { outgoing, imageDataUrl, ...resto } = m;
    void outgoing;
    return {
      ...resto,
      // marca que havia imagem sem guardar os bytes
      ...(imageDataUrl ? { pdfName: undefined, text: m.text || '📷 Foto enviada' } : {}),
    } as Message;
  });
}

export function listarConversas(): ConversaSalva[] {
  try {
    const raw = localStorage.getItem(KEY);
    const lista = raw ? (JSON.parse(raw) as ConversaSalva[]) : [];
    return lista.sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

export function salvarConversa(c: ConversaSalva): void {
  try {
    const lista = listarConversas().filter((x) => x.id !== c.id);
    lista.unshift({ ...c, messages: sanearMensagens(c.messages), updatedAt: Date.now() });
    localStorage.setItem(KEY, JSON.stringify(lista.slice(0, MAX_CONVERSAS)));
  } catch {
    /* storage cheio/indisponível: a conversa atual continua na memória */
  }
}

export function removerConversa(id: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(listarConversas().filter((x) => x.id !== id)));
  } catch {
    /* noop */
  }
}

/** Linha de preview da lista (última mensagem "legível" da conversa). */
export function previewDaConversa(c: ConversaSalva): string {
  for (let i = c.messages.length - 1; i >= 0; i--) {
    const m = c.messages[i];
    if (m.role === 'tool_status') continue;
    if (m.isAudio) return '🎤 Áudio';
    if (m.pdfName) return `📄 ${m.pdfName}`;
    if (m.text) return m.text.replace(/\s+/g, ' ').trim();
    if (m.cotacao) return '🧪 Orçamento personalizado';
  }
  return 'Conversa';
}
